'use client'

import { Send } from '@mui/icons-material'
import { Alert, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import EmailSupport from '~/components/EmailSupport'
import { useOverlayContext } from '~/components/Overlay'
import { useCreateBugReportMutation } from '~/data/api/client'
import { useSnackbarControls } from '~/store/slices/display'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import { buildBugReportParams } from './buildBugReportParams'

type ReportCategory = 'bug' | 'feature' | 'support'

export default function ReportIssueForm() {
  const t = useTranslations('layouts.HelpOverlay')
  const organizationId = useCurrentOrgId()
  const pathname = usePathname()
  const { closeOverlay } = useOverlayContext()
  const { openSnackbar } = useSnackbarControls()
  const [category, setCategory] = useState<ReportCategory>('support')
  const [message, setMessage] = useState('')
  const [submitState, setSubmitState] = useState<'idle' | 'error'>('idle')
  const [createBugReport, { isLoading: isSubmitting }] = useCreateBugReportMutation()

  const canSubmit = Boolean(organizationId && message.trim() && !isSubmitting)

  async function submitMessage() {
    if (!organizationId || !message.trim() || isSubmitting) return

    setSubmitState('idle')
    try {
      await createBugReport({
        'X-Organization-Id': organizationId,
        bugReportCreateParams: buildBugReportParams({
          message: message.trim(),
          category,
          pagePath: pathname
        })
      }).unwrap()
      closeOverlay()
      openSnackbar(t('helpSubmitSuccess'))
    } catch {
      setSubmitState('error')
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{t('helpSendMessage')}</Typography>
      <TextField
        select
        fullWidth
        size="small"
        label={t('helpCategoryLabel')}
        value={category}
        onChange={(event) => {
          setCategory(event.target.value as ReportCategory)
          setSubmitState('idle')
        }}
      >
        <MenuItem value="support">{t('helpCategorySupport')}</MenuItem>
        <MenuItem value="feature">{t('helpCategoryFeature')}</MenuItem>
        <MenuItem value="bug">{t('helpCategoryBug')}</MenuItem>
      </TextField>
      <TextField
        fullWidth
        multiline
        minRows={3}
        placeholder={t('helpMessagePlaceholder')}
        value={message}
        onChange={(event) => {
          setMessage(event.target.value)
          setSubmitState('idle')
        }}
      />
      {submitState === 'error' ? (
        <Alert severity="error">
          {t('helpSubmitError')} <EmailSupport />
        </Alert>
      ) : null}
      <Button
        fullWidth
        variant="contained"
        disabled={!canSubmit}
        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Send fontSize="small" />}
        sx={{ textTransform: 'none' }}
        onClick={() => void submitMessage()}
      >
        {t('helpSendButton')}
      </Button>
    </Stack>
  )
}
